/**
 * Message processing helper functions
 * Testable utilities for processing Gmail messages
 */

import type { GmailMessage, PreviousContactDetails, TraitResults } from '../types';
import { normalizeEmail } from '../scoring/client';

/**
 * Extract email address from "Name <email>" format
 */
export function extractEmailFromFromField(fromField: string): string {
  if (fromField.includes('<')) {
    const match = fromField.match(/<([^>]+)>/);
    return match ? match[1] : fromField;
  }
  return fromField;
}

/**
 * Convert internal_date timestamp to ISO string
 */
export function convertInternalDateToISO(internalDate: string | number | undefined): string | null {
  if (!internalDate) {
    return null;
  }

  try {
    const timestamp = typeof internalDate === 'string' ? parseInt(internalDate, 10) : internalDate;
    if (!isNaN(timestamp) && timestamp > 0) {
      return new Date(timestamp).toISOString();
    }
  } catch {
    // If conversion fails, return null
  }

  return null;
}

/**
 * Build previous contact details from various sources
 */
export function buildPreviousContactDetails(
  fromEmail: string,
  threadHistory: string[] | undefined,
  gmailHasSent: boolean
): PreviousContactDetails {
  const threadHasUserSent = threadHistory?.includes('user_sent') || false;
  const hasPreviousContact = threadHasUserSent || gmailHasSent;

  const source: PreviousContactDetails['source'] = [];
  if (threadHasUserSent) source.push('thread_history');
  if (gmailHasSent) source.push('gmail_sent');
  if (!hasPreviousContact) source.push('none');

  return {
    has_contact: hasPreviousContact,
    source,
    thread_has_user_sent: threadHasUserSent,
    db_has_previous: false,
    gmail_has_sent: gmailHasSent,
  };
}

/**
 * Update trait results with temporal context
 */
export function updateTraitResultsTemporal(
  traitResults: TraitResults,
  receivedAt: string | null,
  actionPreference: string
): void {
  // Update user context with actual action preference
  traitResults.user_context.action_preference = actionPreference;

  // Update temporal context with received_at
  if (receivedAt) {
    traitResults.temporal.received_at = receivedAt;
    const receivedDate = new Date(receivedAt);
    const scoredDate = new Date(traitResults.temporal.scored_at);
    const hoursSinceReceived = (scoredDate.getTime() - receivedDate.getTime()) / (1000 * 60 * 60);
    traitResults.temporal.time_since_received_hours =
      hoursSinceReceived >= 0 ? hoursSinceReceived : null;
  }
}

/**
 * Normalize sender email for consistent matching
 */
export function normalizeSenderEmail(fromField: string): string {
  const email = extractEmailFromFromField(fromField);
  return normalizeEmail(email);
}

/**
 * Extract and normalize sender information for routing rule matching.
 * Returns the normalized email for efficient routing rule checks.
 * Note: Domain extraction is done separately using extractDomain() to avoid circular dependencies.
 *
 * @param fromField - The sender email field (e.g., "Name <user@example.com>" or "user@example.com")
 * @returns Normalized email, or undefined if fromField is empty
 */
export function extractSenderInfo(fromField: string | undefined): {
  normalizedEmail: string | undefined;
} {
  if (!fromField) {
    return { normalizedEmail: undefined };
  }

  const normalizedEmail = normalizeSenderEmail(fromField);

  return { normalizedEmail };
}

/**
 * Create a minimal TraitResults object for cases where spam scoring is bypassed
 * (e.g., routing rule matches). This ensures all required fields are present.
 *
 * @param message - The Gmail message being processed
 * @param threshold - User's spam threshold
 * @param actionPreference - User's action preference
 * @param bypassReason - Reason for bypassing spam scoring (e.g., 'routing_rule_bypass')
 * @param subscriptionTier - User's subscription tier (defaults to 'free')
 * @returns A complete TraitResults object with all required fields initialized
 */
export function createMinimalTraitResults(
  message: GmailMessage,
  threshold: number,
  actionPreference: string,
  bypassReason: string,
  subscriptionTier: string = 'free'
): TraitResults {
  const now = new Date();
  return {
    allowlist_match: false,
    blocklist_match: false,
    previous_contact: {
      has_contact: false,
      source: ['none'],
      thread_has_user_sent: false,
      db_has_previous: false,
      gmail_has_sent: false,
    },
    thread_engagement: {
      has_real_engagement: false,
      checked: false,
      reason: null,
    },
    same_domain: false,
    trusted_domain: false,
    domain_check: {
      domain: null,
      etld1: null,
      is_trusted: false,
      trusted_reason: null,
      mx_records: { exists: false, checked_domains: [], network_error: false },
      a_records: { exists: false, network_error: false },
      redirects: {
        http_status: null,
        https_status: null,
        redirect_location: null,
        is_suspicious: false,
        network_error: false,
      },
      ssl: { valid: true, error: null, network_error: false },
      email_auth: {
        spf: { exists: false, network_error: false },
        dkim: { exists: false, selectors_checked: [], network_error: false },
        dmarc: { exists: false, network_error: false },
      },
    },
    avatar: {
      has_avatar: false,
      source: null,
      avatar_url: null,
      gravatar_checked: false,
      libravatar_checked: false,
      is_empty_silhouette: false,
      etag_match: null,
      content_length_match: null,
      network_error: false,
    },
    reply_to: { address: null, matches_from: true },
    domain_age: { days_old: null, is_new: false, checked: false, api_used: null },
    fingerprints: {
      x_mailer: { present: false, value: null, matched_tool: null, is_generic: false },
      outbound_headers: [],
      outbound_mtas: { detected: [], combined_with_other_factors: false },
      outbound_domains: { detected: [], matched_domains: [] },
    },
    bdr_phrases: {
      high_confidence: { phrases_found: [], count: 0 },
      low_confidence: { phrases_found: [], count: 0, scored: false },
      text_checked: {
        subject_length: message.subject?.length || 0,
        snippet_length: message.snippet?.length || 0,
        total_length: (message.subject?.length || 0) + (message.snippet?.length || 0),
      },
    },
    case_management: {
      detected: false,
      confidence: 'low',
      indicators: {
        domain_match: false,
        email_pattern_match: false,
        header_match: false,
        subject_pattern_match: false,
        from_name_pattern_match: false,
      },
      matched_domain: null,
      matched_email_pattern: null,
      matched_header: null,
      matched_subject_pattern: null,
      matched_from_name_pattern: null,
    },
    sales_intent: {
      is_sales: false,
      checked: false,
      reason: null,
      cached: false,
    },
    auth_results: {
      spf: { value: null, passed: null, present: false },
      dkim: { value: null, passed: null, present: false },
      dmarc: { value: null, passed: null, present: false },
      authentication_results_header: null,
    },
    message_metadata: {
      thread_id: message.thread_id || '',
      thread_size: null,
      is_thread_starter: null,
      has_attachments: message.has_attachments || false,
      attachment_count: message.attachment_count || 0,
      content_type: 'plain',
      body_length: 0,
      link_count: 0,
      image_count: 0,
      to_addresses: [],
      cc_addresses: [],
      bcc_addresses: [],
    },
    subject: message.subject || '',
    snippet: message.snippet || '',
    user_context: {
      threshold,
      action_preference: actionPreference,
      subscription_tier: subscriptionTier,
      allowlist_patterns: [],
      blocklist_patterns: [],
      trusted_domains: [],
      user_gmail_email: null,
    },
    scoring_metadata: {
      checks_performed: [bypassReason],
      checks_skipped: [],
      scoring_version: '1.0.0',
      scoring_duration_ms: 0,
      parallel_checks: false,
    },
    api_calls: {
      domain_check: {
        called: false,
        success: false,
        duration_ms: 0,
        network_error: false,
        timeout: false,
      },
      avatar_check: {
        called: false,
        success: false,
        duration_ms: 0,
        gravatar_checked: false,
        libravatar_checked: false,
        network_error: false,
      },
      domain_age_check: { called: false, success: false, duration_ms: 0, api_used: null },
      llm_trust_check: { called: false, success: false, duration_ms: 0, cached: false },
      llm_sales_intent_check: { called: false, success: false, duration_ms: 0, cached: false },
    },
    action_result: {
      action_applied: false,
      action_type: null,
      action_success: false,
      label_name: null,
      action_error: null,
    },
    temporal: {
      scored_at: now.toISOString(),
      received_at: null,
      time_since_received_hours: null,
      scoring_day_of_week: now.getDay(),
      scoring_hour: now.getHours(),
    },
    errors: {
      domain_check_error: null,
      avatar_check_error: null,
      scoring_error: null,
      network_errors: [],
      timeouts: [],
    },
    score_breakdown: {
      total: 0,
      contributions: [],
    },
  };
}
